import { MAIN_DATASOURCE_NAME } from '../../../constants/dataSource.constants';
import { parseXmlNative } from '../../../utility/xmlHelpers';
import {
    paramAction,
    paramDsName,
    paramPath,
} from '../constants/parameterNames';
import { typeDataSource, typeOption } from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import {
    getNetworkFileDownloadPath,
    performLocalAccessCheck,
} from '../helpers/localFileHelpers';
import { getParamValue } from '../helpers/parameter.accessors';
import { swapDomContents } from '../helpers/swapDomHelpers';

const ACTION_DOWNLOAD = 'download';
const ACTION_LINK = 'link';
const ACTION_LOAD = 'load';

const description =
    "Retrieves a file from a local network. Can initiate a download of the file, generate a URL to download the file, or load the file into one of the form's data sources.";

const requiredParameters = {
    [paramPath]: {
        description: 'The path to the file',
    },
};

const optionalParameters = {
    [paramAction]: {
        description: `(defaults to ${ACTION_DOWNLOAD}) The action to perform with the requested file (${ACTION_DOWNLOAD} - initiate a download; ${ACTION_LINK} - create a download link; ${ACTION_LOAD} - load the file into one of the form's data sources)`,
        type: typeOption,
        options: [
            {
                name: 'Download the file',
                value: ACTION_DOWNLOAD,
            },
            {
                name: 'Obtain a link that can be used to access the file',
                value: ACTION_LINK,
            },
            {
                name: "Load the file into one of the form's data sources",
                value: ACTION_LOAD,
            },
        ],
    },
    [paramDsName]: {
        description: `The name of the data source to load the file into (only applicable when using /${paramAction}=${ACTION_LOAD})`,
        type: typeDataSource,
    },
};

function initiateDownload(path) {
    if (window.open(path)) {
        return success();
    }

    return failure(
        'Unable to initiate download. Please ensure that your browser is not blocking popups from this page.',
    );
}

const loadFileIntoDataSource = async (path, dataSources, dsName, api) => {
    const fileText = await api.getNetworkFileText(path);

    const fileXml = parseXmlNative(fileText).documentElement;

    await swapDomContents(dataSources, dsName, fileXml);

    return success();
};

// (String, String, Api, DataSources, Environment) -> Promise[CommandResult]
const applyAction = (path, action, api, dataSources, dsName, environment) => {
    const downloadPath = getNetworkFileDownloadPath(path, environment);

    switch (action.toLowerCase()) {
        case ACTION_DOWNLOAD:
            return initiateDownload(downloadPath);
        case ACTION_LINK:
            return success(downloadPath);
        case ACTION_LOAD:
            return loadFileIntoDataSource(path, dataSources, dsName, api);
        default:
            return failure(`Unknown /${paramAction} value: ${action}`);
    }
};

const executeAsync = async ({ api, dataSources, environment, parameters }) => {
    const path = getParamValue(parameters, paramPath);
    const action = getParamValue(parameters, paramAction, ACTION_DOWNLOAD);
    const dsName = getParamValue(parameters, paramDsName, MAIN_DATASOURCE_NAME);

    const checkResult = await performLocalAccessCheck(api, path);

    if (checkResult) {
        return checkResult;
    }

    return await applyAction(
        path,
        action,
        api,
        dataSources,
        dsName,
        environment,
    );
};

const GetNetworkFile = {
    description,
    requiredParameters,
    optionalParameters,
    executeAsync,
};

export default GetNetworkFile;
